import React, {useState} from "react";

/* Validation */
import {Formik, useFormikContext} from "formik";
import {FormData, validationSchema} from "./formConfig";

/* API */
import axios from "axios";
import {Endpoints} from "@internal/common";

import CustomError from "./CustomError";
import PurchaseConditionsModal from "./modals/PurchaseConditionsModal";

import {
  Alert,
  Button,
  Combobox,
  Flex,
  HelpText,
  Input,
  InputGroup,
  Label,
  Legend,
  Text,
  Title,
  ValidationMessage,
} from "@gjensidige/builders-components";

const CAStudyInsurance = () => {
  const [submitted, setSubmitted] = useState(false);
  const [apiError, setApiError] = useState(false);

  const sendFormData = (formData: FormData) => {
    setApiError(false);
    axios
      .post(Endpoints.CAStudyInsurance, formData)
      .then(() => setSubmitted(true))
      .catch(() => setApiError(true));
  };

  return (
    <Flex center={"md"} fill layout={1}>
      {submitted ? (
        <Alert severity="success" tracking={{eventLabel: 'tak-for-din-bestilling'}} style={{ whiteSpace: "pre-line" }}>
          <Title>Tak for din bestilling</Title>
          <Text>
            Vi sender dig en mail med din police og forsikringsdetaljer, så snart vi har oprettet forsikringen.
          </Text>
        </Alert>
      ) : (
        <>
          <Formik
            enableReinitialize
            initialValues={{
              bicycleCoverage: false,
              electronicsCoverage: false,
              accidentCoverage: false,
              typeOfTravelInsurance: "",
              name: "",
              cprNumber: "",
              cprConsent: false,
              phoneNumber: "",
              email: "",
              address: "",
              zipCode: "",
              city: "",
              caMember: "",
              registrationNumber: "",
              accountNumber: "",
              purchaseConditionsConsent: false,
              marketingConsent: true,
              marketingConsentEmail: false,
              marketingConsentTextMessage: false,
              marketingConsentTypes: [""],
              hasInsuranceWithOthers: "",
              householdInsuranceDetails: "",
              accidentInsuranceDetails: "",
              travelInsuranceDetails: "",
              terminationValidFrom: "",
            }}
            validationSchema={validationSchema()}
            onSubmit={(formData: FormData) => {
              sendFormData(formData);
            }}
          >
            <RenderForm/>
          </Formik>
        </>
      )}
      {apiError && (
        <Alert severity="error" tracking={{eventLabel: 'der-skete-en-fejl'}}>
          <Title>Der skete en fejl.</Title>
          <Text>
            Der skete en fejl ved indsendelse. Prøv venligst igen senere
          </Text>
        </Alert>
      )}
    </Flex>
  );
};

const RenderForm = () => {
  const {handleSubmit, handleBlur, setFieldValue, touched, errors, values} =
    useFormikContext<FormData>();

  const [showPurchaseModal, setShowPurchaseModal] = useState(false);

  const typeOfTravelInsurances = [
    "Europa inkl. afbestilling (40 kr./mnd)",
    "Verden inkl. afbestilling (60 kr./mnd)",
    "Jeg vil ikke have rejseforsikring",
  ];

  const togglePurchaseModal = () => {
    setShowPurchaseModal(!showPurchaseModal);
  };

  return (
    <form onSubmit={handleSubmit}>
      <Flex gap={"lg"} layout={1}>
        <Flex layout={1}>
          <Title size={3}>
            Udfyld formularen for at bestille din CA studieforsikring
          </Title>
          <Text>
            Tilpas din indboforsikring med tilvalgene cykel og elektronik. Og
            pas ekstragodt på dig selv i hverdagen og på ferier med en super god
            og billig ulykkes- og rejseforsikring.
          </Text>
          <Text size="small">
            Du skal opgive dit CPR-nr. for at kunne gennemføre køb online.{" "}
            <br/>
            <br/>
            Se hvordan vi behandler dine personoplysninger i vores{" "}
            <a
              href="https://www.gjensidige.dk/privat/cookies-og-sikkerhed"
              target="_blank"
              rel="noreferrer"
            >
              privatlivspolitik.
            </a>
            <br/>
            <br/>
            Priserne nedenfor er pr. måned inkl. afgifter.
            <br/>
            <br/>* Påkrævede felter
          </Text>
        </Flex>
        <Flex layout={1}>
          <Text weight={500}>Indboforsikring, obligatorisk (95 kr.)</Text>
          <div>
            <Label htmlFor={"bicycleCoverage-id"}>
              <Input
                id="bicycleCoverage-id"
                name="bicycleCoverage"
                type="checkbox"
                value="bicycleCoverage"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setFieldValue("bicycleCoverage", event.target.checked);
                }}
                onBlur={handleBlur}
                checked={values.bicycleCoverage}
              />
              inkl. cykeldækning (+25 kr.)
            </Label>
          </div>
          <div>
            <Label htmlFor={"electronicsCoverage-id"}>
              <Input
                id="electronicsCoverage-id"
                name="electronicsCoverage"
                type="checkbox"
                value="electronicsCoverage"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setFieldValue("electronicsCoverage", event.target.checked);
                }}
                onBlur={handleBlur}
                checked={values.electronicsCoverage}
              />
              inkl. elektronikdækning (+50 kr.)
            </Label>
            <HelpText>
              Indboforsikringen er standard. Cykel- og elektronikdækning er
              valgfri.
            </HelpText>
          </div>
          <Text weight={500}>Ulykkesforsikring, valgfri</Text>
          <div>
            <Label htmlFor={"accidentCoverage-id"}>
              <Input
                id="accidentCoverage-id"
                name="accidentCoverage"
                type="checkbox"
                value="accidentCoverage"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setFieldValue("accidentCoverage", event.target.checked);
                }}
                onBlur={handleBlur}
                checked={values.accidentCoverage}
              />
              Ulykkesforsikring (50kr.)
              <HelpText>Ulykkesforsikringen er valgfri.</HelpText>
            </Label>
          </div>
          <div>
            <Label htmlFor={"typeOfTravelInsurance"}>
              Rejseforsikring, valgfri
            </Label>
            <Combobox
              name="typeOfTravelInsurance"
              id="typeOfTravelInsurance"
              options={typeOfTravelInsurances.map((option) => {
                return {labelText: option, value: option};
              })}
              values={values.typeOfTravelInsurance}
              onChange={(value) => {
                setFieldValue("typeOfDamage", value);
              }}
              onBlur={handleBlur}
              invalid={
                !!(
                  touched.typeOfTravelInsurance && errors.typeOfTravelInsurance
                )
              }
            />
            <ValidationMessage
              hidden={
                !(touched.typeOfTravelInsurance && errors.typeOfTravelInsurance)
              }
              htmlFor={"typeOfTravelInsurance"}
              invalid={
                !!(
                  touched.typeOfTravelInsurance && errors.typeOfTravelInsurance
                )
              }
            >
              {errors.typeOfTravelInsurance}
            </ValidationMessage>
          </div>
        </Flex>
        <Flex layout={1}>
          <Title size={5}>Om dig</Title>
          <div>
            <Label htmlFor={"name"}>Navn *</Label>
            <Input
              id="name"
              name="name"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setFieldValue("name", event.target.value);
              }}
              onBlur={handleBlur}
              invalid={!!(touched.name && errors.name)}
              value={values.name}
            />
            <ValidationMessage
              hidden={!(touched.name && errors.name)}
              htmlFor={"name"}
              invalid={!!(touched.name && errors.name)}
            >
              {errors.name}
            </ValidationMessage>
          </div>
          <div>
            <Label htmlFor={"cprNumber"}>CPR-nr. *</Label>
            <Input
              id="cprNumber"
              name="cprNumber"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setFieldValue("cprNumber", event.target.value);
              }}
              onBlur={handleBlur}
              invalid={!!(touched.cprNumber && errors.cprNumber)}
              value={values.cprNumber}
            />
            <ValidationMessage
              hidden={!(touched.cprNumber && errors.cprNumber)}
              htmlFor={"cprNumber"}
              invalid={!!(touched.cprNumber && errors.cprNumber)}
            >
              {errors.cprNumber}
            </ValidationMessage>
          </div>
          <div>
            <Label htmlFor={"cprConsent-id"}>
              <Input
                id="cprConsent-id"
                name="cprConsent"
                type="checkbox"
                onChange={(event) => {
                  setFieldValue("cprConsent", event.target.checked);
                }}
                onBlur={handleBlur}
                checked={values.cprConsent}
              />
              Ja til behandling af mit CPR-nr. Jeg giver hermed samtykke til, at
              Gjensidige må behandle mit CPR-nr. til brug for kommunikation via
              e-Boks og til brug for entydig identifikation af mig. Du kan når
              som helst tilbagekalde dit samtykke på DinSide eller ved at
              kontakte Gjensidige Forsikring. Se hvordan i vores
              privatlivspolitik, som du finder på gjensidige.dk, hvor du også
              kan læse mere om vores behandling af personoplysninger. Hvis
              samtykke ikke gives, eller det tilbagekaldes, kan Gjensidige
              blandt andet ikke kommunikere via e-Boks eller foretage udbetaling
              til din NemKonto. *
              <HelpText htmlFor={"cprConsent-id"}>
                {`Vi har brug for dit samtykke til "Behandling af mit CPR-nr." for at kunne forsikre netop dig. Svarer du "Ja", er vi sikre på, at du er den rigtige person, vi forsikrer, og som vi kan overføre evt. erstatning til på din NemKonto, sende til e-Boks mv. Dette gælder både lovpligtige og ikke lovpligtige forsikringer. ​Gjensidige Med Gjensidige mener vi Gjensidige Forsikring, dansk filial af Gjensidige Forsikring ASA Norge herunder Gjensidige-ejede brands som Gouda Rejseforsikring og Byggeriets Forsikringsservice."`}
              </HelpText>
            </Label>
          </div>
          {touched.cprConsent && errors.cprConsent && (
            <CustomError errorText={errors.cprConsent}/>
          )}
          <div>
            <Label htmlFor={"phoneNumber"}>Telefonnr. *</Label>
            <Input
              id="phoneNumber"
              name="phoneNumber"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setFieldValue("phoneNumber", event.target.value);
              }}
              onBlur={handleBlur}
              invalid={!!(touched.phoneNumber && errors.phoneNumber)}
              value={values.phoneNumber}
            />
            <ValidationMessage
              hidden={!(touched.phoneNumber && errors.phoneNumber)}
              htmlFor={"phoneNumber"}
              invalid={!!(touched.phoneNumber && errors.phoneNumber)}
            >
              {errors.phoneNumber}
            </ValidationMessage>
          </div>
          <div>
            <Label htmlFor={"email"}>Email *</Label>
            <Input
              id="email"
              name="email"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setFieldValue("email", event.target.value);
              }}
              onBlur={handleBlur}
              invalid={!!(touched.email && errors.email)}
              value={values.email}
            />
            <ValidationMessage
              hidden={!(touched.email && errors.email)}
              htmlFor={"email"}
              invalid={!!(touched.email && errors.email)}
            >
              {errors.email}
            </ValidationMessage>
          </div>

          <div>
            <Label htmlFor={"address"}>Vejnavn *</Label>
            <Input
              id="address"
              name="address"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setFieldValue("address", event.target.value);
              }}
              onBlur={handleBlur}
              invalid={!!(touched.address && errors.address)}
              value={values.address}
            />
            <ValidationMessage
              hidden={!(touched.address && errors.address)}
              htmlFor={"address"}
              invalid={!!(touched.address && errors.address)}
            >
              {errors.address}
            </ValidationMessage>
          </div>
          <div>
            <Label htmlFor={"zipCode"}>Postnr. *</Label>
            <Input
              name="zipCode"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setFieldValue("zipCode", event.target.value);
              }}
              onBlur={handleBlur}
              invalid={!!(touched.zipCode && errors.zipCode)}
              value={values.zipCode}
            />
            <ValidationMessage
              hidden={!(touched.zipCode && errors.zipCode)}
              htmlFor={"zipCode"}
              invalid={!!(touched.zipCode && errors.zipCode)}
            >
              {errors.zipCode}
            </ValidationMessage>
          </div>

          <div>
            <Label htmlFor={"city"}>By *</Label>
            <Input
              id="city"
              name="city"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setFieldValue("city", event.target.value);
              }}
              onBlur={handleBlur}
              invalid={!!(touched.city && errors.city)}
              value={values.city}
            />
            <ValidationMessage
              hidden={!(touched.city && errors.city)}
              htmlFor={"city"}
              invalid={!!(touched.city && errors.city)}
            >
              {errors.city}
            </ValidationMessage>
          </div>
          <InputGroup
            variant={"column"}
            id="caMember"
            name="caMember"
            onChange={(value) => {
              setFieldValue("caMember", value.at(0));
            }}
            type="radio"
            value={values.caMember}
            onBlur={handleBlur}
            invalid={!!(touched.caMember && errors.caMember)}
          >
            <Legend>Er du CA-medlem? *</Legend>
            <InputGroup.Input label="Ja" value="Ja"/>
            <InputGroup.Input label="Nej" value="Nej"/>
            <ValidationMessage
              hidden={!(touched.caMember && errors.caMember)}
              invalid={!!(touched.caMember && errors.caMember)}
            >
              {errors.caMember}
            </ValidationMessage>
          </InputGroup>
        </Flex>
        <Flex layout={1}>
          <Title size={5}>Bankoplysninger</Title>
          <Text>
            Noter dit registrerings- og kontonr. så tilmelder vi automatisk din
            forsikring til Betalingsservice.
          </Text>

          <div>
            <Label htmlFor={"registrationNumber"}>Registreringsnr. *</Label>
            <Input
              id="registrationNumber"
              name="registrationNumber"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setFieldValue("registrationNumber", event.target.value);
              }}
              onBlur={handleBlur}
              invalid={
                !!(touched.registrationNumber && errors.registrationNumber)
              }
              value={values.registrationNumber}
            />
            <ValidationMessage
              hidden={
                !(touched.registrationNumber && errors.registrationNumber)
              }
              htmlFor={"registrationNumber"}
              invalid={
                !!(touched.registrationNumber && errors.registrationNumber)
              }
            >
              {errors.registrationNumber}
            </ValidationMessage>
          </div>

          <div>
            <Label htmlFor={"accountNumber"}>Kontonr. *</Label>
            <Input
              id="accountNumber"
              name="accountNumber"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setFieldValue("accountNumber", event.target.value);
              }}
              onBlur={handleBlur}
              invalid={!!(touched.accountNumber && errors.accountNumber)}
              value={values.accountNumber}
            />
            <ValidationMessage
              hidden={!(touched.accountNumber && errors.accountNumber)}
              htmlFor={"accountNumber"}
              invalid={!!(touched.accountNumber && errors.accountNumber)}
            >
              {errors.accountNumber}
            </ValidationMessage>
          </div>
        </Flex>
        <Flex layout={1}>
          <Title size={5}>Betingelser for køb online</Title>
          <Text>
            Jeg har givet korrekte oplysninger og godkender{" "}
            <span
              onClick={() => setShowPurchaseModal(true)}
              style={{
                fontWeight: 800,
                textDecoration: "underline",
                cursor: "pointer",
              }}
            >
              vilkår og betingelser
            </span>{" "}
            for køb på gjensidige.dk
          </Text>
          <div>
            <Label htmlFor={"accountNumber"}>
              <Input
                required
                id="purchaseConditionsConsent-id"
                name="purchaseConditionsConsent"
                type="checkbox"
                value="purchaseConditionsConsent"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setFieldValue(
                    "purchaseConditionsConsent",
                    event.target.checked,
                  );
                }}
                onBlur={handleBlur}
                checked={values.purchaseConditionsConsent}
              />
              Bekræft *
            </Label>

            <ValidationMessage
              hidden={
                !(
                  touched.purchaseConditionsConsent &&
                  errors.purchaseConditionsConsent
                )
              }
              htmlFor={"purchaseConditionsConsent-id"}
              invalid={
                !!(
                  touched.purchaseConditionsConsent &&
                  errors.purchaseConditionsConsent
                )
              }
            >
              {errors.purchaseConditionsConsent}
            </ValidationMessage>
          </div>

          <PurchaseConditionsModal
            showModal={showPurchaseModal}
            toggleModal={togglePurchaseModal}
          />
          {touched.purchaseConditionsConsent &&
            errors.purchaseConditionsConsent && (
              <>
                <br/>
                <CustomError errorText={errors.purchaseConditionsConsent}/>
              </>
            )}
        </Flex>

        <Flex layout={1}>
          <Title size={5}>Opsigelse af eksisterende forsikringer</Title>
          <div>
            <InputGroup
              variant={"column"}
              id="hasInsuranceWithOthers"
              name="hasInsuranceWithOthers"
              onChange={(value) => {
                setFieldValue("hasInsuranceWithOthers", value.at(0));
              }}
              type="radio"
              value={values.hasInsuranceWithOthers}
              onBlur={handleBlur}
              invalid={
                !!(
                  touched.hasInsuranceWithOthers &&
                  errors.hasInsuranceWithOthers
                )
              }
            >
              <Legend>
                Har du allerede en forsikring i et andet selskab, som vi skal
                opsige? *
              </Legend>
              <InputGroup.Input label="Ja" value="Ja"/>
              <InputGroup.Input label="Nej" value="Nej"/>
              <ValidationMessage
                hidden={
                  !(
                    touched.hasInsuranceWithOthers &&
                    errors.hasInsuranceWithOthers
                  )
                }
                invalid={
                  !!(
                    touched.hasInsuranceWithOthers &&
                    errors.hasInsuranceWithOthers
                  )
                }
              >
                {errors.hasInsuranceWithOthers}
              </ValidationMessage>
            </InputGroup>
          </div>
          {values.hasInsuranceWithOthers === "Ja" ? (
            <>
              <Text>
                Hvis vi skal opsige flere forsikringer for dig i forskellige
                forsikringsselskaber, er det vigtigt, at du informerer os om dem
                alle.
              </Text>
              <div>
                <Label htmlFor={"householdInsuranceDetails"}>
                  Indboforsikring
                </Label>
                <Input
                  id="householdInsuranceDetails"
                  placeholder="Forsikringsselskab"
                  name="householdInsuranceDetails"
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setFieldValue(
                      "householdInsuranceDetails",
                      event.target.value,
                    );
                  }}
                  onBlur={handleBlur}
                  invalid={
                    !!(
                      touched.householdInsuranceDetails &&
                      errors.householdInsuranceDetails
                    )
                  }
                  value={values.householdInsuranceDetails}
                />
                <ValidationMessage
                  htmlFor={"householdInsuranceDetails"}
                  hidden={
                    !(
                      touched.householdInsuranceDetails &&
                      errors.householdInsuranceDetails
                    )
                  }
                  invalid={
                    !!(
                      touched.householdInsuranceDetails &&
                      errors.householdInsuranceDetails
                    )
                  }
                >
                  {errors.householdInsuranceDetails}
                </ValidationMessage>
              </div>

              <div>
                <Label htmlFor={"accidentInsuranceDetails"}>
                  Ulykkesforsikring
                </Label>
                <Input
                  id="accidentInsuranceDetails"
                  placeholder="Forsikringsselskab"
                  name="accidentInsuranceDetails"
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setFieldValue(
                      "accidentInsuranceDetails",
                      event.target.value,
                    );
                  }}
                  onBlur={handleBlur}
                  invalid={
                    !!(
                      touched.accidentInsuranceDetails &&
                      errors.accidentInsuranceDetails
                    )
                  }
                  value={values.accidentInsuranceDetails}
                />
                <ValidationMessage
                  htmlFor={"accidentInsuranceDetails"}
                  hidden={
                    !(
                      touched.accidentInsuranceDetails &&
                      errors.accidentInsuranceDetails
                    )
                  }
                  invalid={
                    !!(
                      touched.accidentInsuranceDetails &&
                      errors.accidentInsuranceDetails
                    )
                  }
                >
                  {errors.accidentInsuranceDetails}
                </ValidationMessage>
              </div>

              <div>
                <Label htmlFor={"travelInsuranceDetails"}>
                  Rejseforsikring
                </Label>
                <Input
                  id="travelInsuranceDetails"
                  placeholder="Forsikringsselskab"
                  name="travelInsuranceDetails"
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setFieldValue("travelInsuranceDetails", event.target.value);
                  }}
                  onBlur={handleBlur}
                  invalid={
                    !!(
                      touched.travelInsuranceDetails &&
                      errors.travelInsuranceDetails
                    )
                  }
                  value={values.travelInsuranceDetails}
                />
                <ValidationMessage
                  htmlFor={"travelInsuranceDetails"}
                  hidden={
                    !(
                      touched.travelInsuranceDetails &&
                      errors.travelInsuranceDetails
                    )
                  }
                  invalid={
                    !!(
                      touched.travelInsuranceDetails &&
                      errors.travelInsuranceDetails
                    )
                  }
                >
                  {errors.travelInsuranceDetails}
                </ValidationMessage>
              </div>

              <InputGroup
                variant={"column"}
                id="terminationValidFrom"
                name="terminationValidFrom"
                onChange={(value) => {
                  setFieldValue("terminationValidFrom", value.at(0));
                }}
                type="radio"
                value={values.terminationValidFrom}
                onBlur={handleBlur}
                invalid={
                  !!(
                    touched.terminationValidFrom && errors.terminationValidFrom
                  )
                }
              >
                <Legend>Hvornår skal opsigelsen gælde fra? *</Legend>
                <InputGroup.Input
                  label="Hurtigst muligt"
                  value="Hurtigst muligt"
                />
                <InputGroup.Input
                  label="Ved min nuværende forsikrings hovedforfald"
                  value="Ved min nuværende forsikrings hovedforfald"
                />
                <ValidationMessage
                  hidden={
                    !(
                      touched.terminationValidFrom &&
                      errors.terminationValidFrom
                    )
                  }
                  invalid={
                    !!(
                      touched.terminationValidFrom &&
                      errors.terminationValidFrom
                    )
                  }
                >
                  {errors.terminationValidFrom}
                </ValidationMessage>
              </InputGroup>
            </>
          ) : null}
        </Flex>
        <Button variant={"primary"} type="submit" flexible>
          Send
        </Button>
      </Flex>
    </form>
  );
};

export default CAStudyInsurance;
